import React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Mail, MessageCircle, Phone, Menu, X, BadgeCheck } from "lucide-react";
import { track } from "@/lib/track";
import { assetSrc } from "@/lib/api";

const NAV_ITEMS = [
  ["About", "about"],
  ["Buy", "buy"],
  ["Rent", "rent"],
  ["Sell", "sell"],
  ["New Launches", "new-launches"],
  ["Financials", "financials"],
  ["Market Insights", "market-insights"],
];

export default function AgentNav({ agent }) {
  const { slug: routeSlug } = useParams();
  const slug = agent?.slug || routeSlug;
  const loc = useLocation();
  const [open, setOpen] = React.useState(false);
  if (!agent) return null;

  const base = `/agent/${slug}`;
  const waNum = (agent.phone || "+919999999999").replace(/[^0-9]/g, "");
  const waHref = `https://wa.me/${waNum}?text=${encodeURIComponent(`Hi ${agent.name}, I came in via your INPropPortal page.`)}`;
  const onWa = () => track({ event_type: "wa_click", agent_slug: slug, source: "microsite" });
  const onCall = () => track({ event_type: "call_click", agent_slug: slug, source: "microsite" });

  const isActive = (key) => {
    if (key === "about") return loc.pathname === base || loc.pathname === `${base}/about`;
    return loc.pathname === `${base}/${key}`;
  };

  return (
    <header className="bg-sg-navy text-white sticky top-0 z-30" data-testid="agent-nav">
      {/* Identity row */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          <Link to={base} className="flex items-center gap-3 min-w-0" data-testid="agent-nav-identity">
            {agent.logo_url ? (
              <img
                src={assetSrc(agent.logo_url)}
                alt={agent.agency_name || agent.name}
                className="h-10 w-10 rounded-md object-contain bg-white/95 p-1 border border-sg-gold/30 shrink-0"
                data-testid="agent-nav-logo"
              />
            ) : (
              <img
                src={agent.picture || "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=120&w=120"}
                alt={agent.name}
                className="w-10 h-10 rounded-full object-cover border border-sg-gold/40 shrink-0"
                data-testid="agent-nav-avatar"
              />
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/55">
                {agent.agency_name ? "Presented by · " + agent.agency_name : "Presented by"}
              </div>
              <div className="font-display text-lg truncate">{agent.name}</div>
            </div>
            <span className="hidden md:inline chip chip-gold ml-3" data-testid="agent-nav-rera">
              <BadgeCheck className="w-3.5 h-3.5 mr-1" /> MahaCRITI: {agent.maharera_number}
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <a href={waHref} target="_blank" rel="noreferrer" onClick={onWa} className="pill-solid inline-flex items-center gap-2" data-testid="agent-nav-wa">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            {agent.phone && (
              <a href={`tel:${agent.phone}`} onClick={onCall} className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy hidden md:inline-flex items-center gap-2" data-testid="agent-nav-call">
                <Phone className="w-4 h-4" /> Call
              </a>
            )}
            <a href="mailto:" className="pill-outline text-white border-white/40 hover:bg-white hover:text-sg-navy hidden md:inline-flex items-center gap-2" data-testid="agent-nav-email">
              <Mail className="w-4 h-4" /> Email
            </a>
            <button
              className="lg:hidden text-white/85 ml-2"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle nav"
              data-testid="agent-nav-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      {/* Nav row */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-12 flex items-center gap-7 text-sm">
          {NAV_ITEMS.map(([label, key]) => {
            const active = isActive(key);
            return (
              <Link
                key={key}
                to={key === "about" ? base : `${base}/${key}`}
                className={`transition-colors ${active ? "text-sg-gold" : "text-white/75 hover:text-sg-gold"}`}
                data-testid={`agent-nav-${key}`}
              >
                {label}
              </Link>
            );
          })}
        </div>
      </div>
      {/* Mobile nav drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-sg-navy-deep" data-testid="agent-nav-mobile-panel">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {NAV_ITEMS.map(([label, key]) => {
              const active = isActive(key);
              return (
                <Link
                  key={key}
                  to={key === "about" ? base : `${base}/${key}`}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-2.5 rounded-lg text-sm ${active ? "bg-sg-gold/15 text-sg-gold" : "text-white/85"}`}
                  data-testid={`agent-nav-mobile-${key}`}
                >
                  {label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
